import React, { useContext } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Form } from 'semantic-ui-react';
import { DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from 'src/app.constants';
import StoredAccordion from 'src/components/StoredAccordion';
import { GenericScaledField } from 'src/components/controls/rhf/GenericScaledField';
import { LatLngCoordinateInput } from 'src/components/controls/rhf/LatLngCoordinateInput';
import { MGRSCoordinateInput } from 'src/components/controls/rhf/MGRSCoordinateInput';
import { UTMCoordinateInput } from 'src/components/controls/rhf/UTMCoordinateInput';
import messages from 'src/messages';
import Offset from './Offset';
import { get } from 'lodash';
import { FormResetContext } from '../utils';
import { CustomFieldEditor } from 'src/components/CustomFieldEditor';

function NDGeneralPanel(props) {
  const resetValue = useContext(FormResetContext);
  const { formatMessage } = useIntl();
  const { control, getFieldState } = useFormContext();
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const prefs = useSelector((state: any) => state.mainFrame.prefs);
  const { title, ap, sm, setModified, setResetValue, isAp = true } = props;
  const apOrSm = isAp ? ap : sm;
  const siteLink = isAp
    ? `/network_sites/${apOrSm.site.id}`
    : `/subscriber_sites/${apOrSm.site.id}`;
  const changeHandler = (fn) => (e) => {
    setModified(true);
    return fn(e);
  };
  const resetCoordinates = () => {
    setModified(true);
    setResetValue((prev) => prev + 1);
  };

  // Watch latitude and longitude values from the form
  const antennaLat = useWatch({ control, name: 'radios.0.antennas.0.latitude' });
  const antennaLng = useWatch({ control, name: 'radios.0.antennas.0.longitude' });

  // Determine which coordinate fields to show
  const latLngFormat = prefs?.latLngFormat;
  const isUTM_NS = latLngFormat === formatMessage(messages.utmNorthSouthHemisphere);
  const isUTM_Band = latLngFormat === formatMessage(messages.utmWithLatitudeBand);
  const isUTM = isUTM_NS || isUTM_Band;
  const isMGRS = latLngFormat === formatMessage(messages.mgrs);
  const showLatLngInputs = !isMGRS && !isUTM;

  // Create a site-like object for the coordinate input components
  // They expect latitude/longitude at the top level, but we need to map from antenna coordinates
  const antennaSite = {
    latitude: antennaLat ?? get(ap, 'radios.0.antennas.0.latitude') ?? get(ap, 'site.latitude'),
    longitude: antennaLng ?? get(ap, 'radios.0.antennas.0.longitude') ?? get(ap, 'site.longitude'),
  };

  let errors = false;
  const fields = [
    'radios.0.antennas.0.latitude',
    'radios.0.antennas.0.longitude',
    'offset',
    'properties.mac_address',
    'properties.msn',
  ];
  errors = fields.some((field) => {
    const { error } = getFieldState(field);
    return error;
  });
  return (
    <StoredAccordion
      name="nd_general_panel"
      title={title}
      headerClass={errors ? 'custom-error' : ''}
      contentProps={{ loading: apOrSm.dirty }}
    >
      <Controller
        control={control}
        name="name"
        defaultValue={apOrSm.name}
        render={({ field: { ref, onChange, ...rest } }) => (
          <Form.Input
            label="Name"
            {...rest}
            onChange={changeHandler(onChange)}
            disabled={!permissionWrite}
            maxLength={NAME_MAX_LENGTH}
          />
        )}
      />
      <div>
        <div>Site Name</div>
        <Link
          to={siteLink}
          title={`${formatMessage(messages.viewSite)}`}
          style={{ display: 'inline-block', margin: '10px 0 10px 8px' }}
        >
          {apOrSm.site.name}
        </Link>
      </div>
      {isAp && (
        <>
          {showLatLngInputs && (
            <LatLngCoordinateInput
              site={antennaSite}
              setModified={setModified}
              disabled={!permissionWrite}
              resetValue={resetValue}
              latitudeField="radios.0.antennas.0.latitude"
              longitudeField="radios.0.antennas.0.longitude"
            />
          )}
          {isMGRS && (
            <MGRSCoordinateInput
              site={antennaSite}
              setModified={setModified}
              disabled={!permissionWrite}
              latitudeField="radios.0.antennas.0.latitude"
              longitudeField="radios.0.antennas.0.longitude"
              mgrsReferenceField="radios.0.antennas.0.mgrs_reference"
            />
          )}
          {isUTM && (
            <UTMCoordinateInput
              site={antennaSite}
              setModified={setModified}
              disabled={!permissionWrite}
              latitudeField="radios.0.antennas.0.latitude"
              longitudeField="radios.0.antennas.0.longitude"
              utmReferenceField="radios.0.antennas.0.utm_reference"
            />
          )}
          <Offset
            key="site.offset"
            ap={ap}
            resetCoordinates={resetCoordinates}
            heightUnits={prefs.heightUnits}
          ></Offset>
        </>
      )}

      <Form.Group widths="equal">
        <GenericScaledField
          key="radios.0.mac_address"
          defaultValue={get(apOrSm, 'radios.0.mac_address')}
          label="MAC Address"
          width={16}
          setModified={setModified}
          watch={resetValue}
          units={prefs['macAddressFormat']}
          getter="radios.0.mac_address"
          disabled={!permissionWrite}
        />
        <GenericScaledField
          key="properties.msn"
          defaultValue={apOrSm.properties.msn}
          label="MSN"
          width={16}
          units="msn"
          watch={resetValue}
          setModified={setModified}
          getter="properties.msn"
          disabled={!permissionWrite}
        />
      </Form.Group>
      <Controller
        control={control}
        name="description"
        defaultValue={apOrSm.description}
        render={({ field: { ref, onChange, ...rest } }) => (
          <Form.TextArea
            label="Description"
            {...rest}
            onChange={changeHandler(onChange)}
            disabled={!permissionWrite}
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
        )}
      />
      <CustomFieldEditor
        category={isAp ? 'access_point' : 'subscriber'}
        propertiesPath="properties"
        setModified={setModified}
      />
    </StoredAccordion>
  );
}

export default NDGeneralPanel;
